import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Clock, Feather } from "lucide-react";
import { LEARN_ARTICLES } from "../../lib/learnArticles";
import { fetchBlog } from "../../lib/api";

const STOP = new Set(["the", "and", "your", "for", "with", "that", "what", "when", "how", "you", "from", "this", "one", "every", "should", "does", "need", "family", "documents", "checklist", "plan", "guide", "keep", "where", "them", "they", "have", "list"]);
export const words = (t) => (t || "").toLowerCase().split(/[^a-z0-9]+/).filter((w) => w.length > 3 && !STOP.has(w));

const overlap = (bag, text) => words(text).filter((w) => bag.has(w)).length;

// Two Learn guides that share the most vocabulary with a blog post.
export const relatedGuides = (p, n = 2) => {
  const bag = new Set([...words(p.title), ...words(p.excerpt), ...(p.tags || []).flatMap(words)]);
  return LEARN_ARTICLES
    .map((a) => ({ a, score: overlap(bag, `${a.title} ${a.description}`) + (a.category === p.category ? 2 : 0) }))
    .filter((x) => x.score > 0).sort((x, y) => y.score - x.score).slice(0, n).map((x) => x.a);
};

// Newest blog notes that share vocabulary with a Learn guide (ties broken by recency).
export const relatedPosts = (a, posts, n = 3) => {
  const bag = new Set(words(`${a.title} ${a.description}`));
  return (posts || [])
    .map((p) => ({ p, score: overlap(bag, `${p.title} ${p.excerpt} ${(p.tags || []).join(" ")}`) + (p.category === a.category ? 2 : 0) }))
    .filter((x) => x.score > 0)
    .sort((x, y) => y.score - x.score || (y.p.published_at || "").localeCompare(x.p.published_at || ""))
    .slice(0, n).map((x) => x.p);
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "");

// Bottom of a Learn guide: the newest related Field Notes, so the two libraries feed each other.
export const RelatedNotes = ({ article }) => {
  const { data } = useQuery({ queryKey: ["blog", "", ""], queryFn: () => fetchBlog(), staleTime: 300_000 });
  const posts = relatedPosts(article, data?.posts);
  if (!posts.length) return null;
  return (
    <section className="mt-14" data-testid="related-notes">
      <div className="flex items-baseline gap-3">
        <h2 className="font-serif text-2xl">From Field Notes</h2>
        <Link to="/blog" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="related-notes-all"><Feather className="h-3 w-3" /> all notes</Link>
      </div>
      <div className="mt-4 grid sm:grid-cols-3 gap-4">
        {posts.map((p) => (
          <Link key={p.id} to={`/blog/${p.slug}`} data-testid={`related-note-${p.slug}`} className="group rounded-2xl border border-border bg-card overflow-hidden flex flex-col hover:border-primary transition-colors">
            <img src={p.cover_url} alt="" loading="lazy" className="aspect-[1200/630] w-full object-cover" />
            <div className="p-4 flex flex-col flex-1">
              <div className="text-[10px] uppercase tracking-widest font-mono text-primary">{p.category}</div>
              <div className="font-serif text-lg leading-snug mt-1 group-hover:text-primary transition-colors">{p.title}</div>
              <div className="mt-2 text-xs text-muted-foreground flex items-center gap-1.5"><Clock className="h-3 w-3" /> {p.read_minutes} min · {fmt(p.published_at)} <ArrowRight className="h-3 w-3 ml-auto group-hover:translate-x-0.5 transition-transform" /></div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
};
